import { Box, Typography } from "@mui/material";
import { neutral, NO_TRANSLATE_CLASS, Text } from "@flat-ui";
import {
  getProjectDateLabel,
  getProjectDateBySort,
} from "@utils/project-utils";
import { PROJECTS_SORT_BY_MAP } from "@components/projects/projects-sort-constants";
import { BaseProjectProps } from "@custom-types/project-types";
import { cardProperties, DASH } from "@utils/ui-utils";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { ProjectDateTooltip } from "@components/common/project-card/project-date-tooltip";
import { useDateTime } from "@hooks/use-date-time";

interface CardMiddleProps extends BaseProjectProps {
  /** The field by which the projects are sorted */
  sortBy: FaroCoreApiTypes.ProjectsSortBy;
}

/**
 * Middle section of the card that shows project name and date information.
 */
export function CardMiddle({ project, sortBy }: CardMiddleProps): JSX.Element {
  const projectName = <var>{project.name}</var>;
  const { formatDateWithTooltip } = useDateTime();

  /** Determine the sort field from the sortBy prop */
  const sortField = PROJECTS_SORT_BY_MAP[sortBy].field;

  const projectDate = getProjectDateBySort(project, sortField);
  const formattedDate = projectDate
    ? formatDateWithTooltip(projectDate).displayDate
    : DASH;

  const projectDateLabel = getProjectDateLabel(sortField);

  return (
    <Box
      component="div"
      sx={{
        paddingTop: cardProperties.creationDatePaddingTop,
        paddingBottom: 0,
        paddingX: "14px",
        height: "100px",
      }}
    >
      <SphereTooltip title={<ProjectDateTooltip project={project} />}>
        <Text variant="bodyS">
          {projectDateLabel}
          <span className={NO_TRANSLATE_CLASS}>: {formattedDate}</span>
        </Text>
      </SphereTooltip>
      <SphereTooltip title={projectName}>
        <Typography
          gutterBottom
          component="div"
          sx={{
            fontSize: "16px",
            color: neutral[800],
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            // !: Do not use ...withEllipsis
            // because we don't need whiteSpace: "nowrap" in order to
            // break the line in two rows.
            wordBreak: "break-word",
            lineHeight: "24px",
            // These properties allow to show an ellipsis on two lines
            // Unfortunately it is only allowed in webkit because there's
            // no native support from CSS for this, only for one line.
            display: "-webkit-box",
            WebkitLineClamp: "2",
            WebkitBoxOrient: "vertical",
          }}
        >
          {projectName}
        </Typography>
      </SphereTooltip>
    </Box>
  );
}
